import { useTranslation } from 'react-i18next';
import { createContext, useState } from 'react';
import { Box, Flex } from 'reflexbox';

import { AddIcon, TargetIcon } from '@treams/treams-icons';
import { Button, Empty, Heading, Lazy, Skeleton, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import GoalsList from '../GoalsList';
import GroupSelector, { InvisibleGroupSelector } from '../group-selector/GroupSelector';

export const CollectiveGoalsContext = createContext<{ groupID?: string }>({ groupID: undefined });

export function CollectiveGoalsList(props: {
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	hideArchived?: boolean;
}) {
	const { t } = useTranslation();
	const [selection, setSelection] = useState<graphql.Group[]>([]);

	const { isGroupManager, isWorkspaceSuperuser, loading } = useViewerRoles();

	return (
		<>
			{loading ? (
				<GroupSelector.Skeleton />
			) : isWorkspaceSuperuser || isGroupManager ? (
				<GroupSelector
					remember
					canSelect={(group) => group.viewerCanViewGoals}
					onChange={setSelection}
				/>
			) : (
				<InvisibleGroupSelector
					canSelect={(group) => group.viewerCanViewGoals}
					onChange={setSelection}
				/>
			)}
			{selection.length === 0 ? (
				<Skeleton.Empty
					contents={
						<Empty
							icon={<TargetIcon />}
							title={t('goals.collective.emptySelection.title')}
							description={t('goals.collective.emptySelection.description')}
						/>
					}
				>
					<GroupGoals.Skeleton />
				</Skeleton.Empty>
			) : (
				selection.map((group) => (
					<Lazy key={group.id}>
						<GroupGoals
							group={group}
							hideArchived={props.hideArchived}
							selectedGoal={props.selectedGoal}
							onSelect={props.onSelect}
						/>
					</Lazy>
				))
			)}
		</>
	);
}

export function GroupGoals(props: {
	group: graphql.Group;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	hideArchived?: boolean;
}) {
	const { t } = useTranslation();

	const query = graphql.useGroupGoalsQuery({
		variables: { id: props.group.id, status: graphql.GoalStatus.Active, last: 100 },
		fetchPolicy: 'network-only',
	});
	const completedQuery = graphql.useGroupGoalsQuery({
		variables: { id: props.group.id, status: graphql.GoalStatus.Completed, last: 100 },
		fetchPolicy: 'network-only',
	});
	const archivedQuery = graphql.useGroupGoalsLazyQuery({
		variables: { id: props.group.id, status: graphql.GoalStatus.Archived, last: 100 },
		fetchPolicy: 'network-only',
	});

	return (
		<Box>
			<Flex justifyContent="space-between" mt={4} mb={2} alignItems="center">
				<Box flexGrow={1} flexShrink={1}>
					<Heading>{props.group.name}</Heading>
					<Subheading>
						{t(`group.goalsV2.teamOrDepartment.type.label.${props.group.type.toLowerCase()}`)}
					</Subheading>
				</Box>

				{props.group.viewerCanCreateGoal && !props.onSelect && (
					<Button
						ml={2}
						icon={<AddIcon />}
						title={t('goals.index.toolbar.new')}
						link={`/goals/new?group_id=${props.group.id}`}
						small
					/>
				)}
			</Flex>
			<GoalsList
				query={query}
				path="group.goalsV2"
				completedQuery={completedQuery}
				archivedQuery={
					props.group.viewerCanViewArchivedGoals && !props.hideArchived ? archivedQuery : undefined
				}
				user={undefined}
				selectedGoal={props.selectedGoal}
				onSelect={props.onSelect}
				reorderable={props.group.viewerCanCreateGoal}
			/>
		</Box>
	);
}

GroupGoals.Skeleton = function GroupGoalsSkeleton() {
	return (
		<Box mt={3}>
			<Heading.Skeleton />
			<GoalsList.Skeleton />
		</Box>
	);
};
